import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// 🔹 Tipo de cada elemento que puede registrarse en el índice de búsqueda
export type SearchItem = {
  id: string;
  title: string;
  content?: string;
  path?: string;
  type?: string;
  tags?: string[];
};

// 🔹 Tipo del contexto de búsqueda
interface SearchContextType {
  query: string;
  setQuery: (q: string) => void;
  results: SearchItem[];
  registerItem: (item: SearchItem) => void;
  unregisterItem: (id: string) => void;
}

// 🔹 Crear contexto
const SearchContext = createContext<SearchContextType | undefined>(undefined);

// 🔹 Hook personalizado para consumirlo
export const useSearch = () => {
  const ctx = useContext(SearchContext);

  if (!ctx) throw new Error("useSearch debe usarse dentro de SearchProvider");
  return ctx;
};

// 🔹 Proveedor principal
export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState<SearchItem[]>([]);
  const [results, setResults] = useState<SearchItem[]>([]);

  // 🔹 Registrar un elemento (profesión, profesional, sección, etc.)
  const registerItem = (item: SearchItem) => {
    setIndex((prev) => {
      const existing = prev.find((i) => i.id === item.id);

      // Si existe y no cambió, no lo reemplazamos
      if (
        existing &&
        existing.title === item.title &&
        existing.content === item.content &&
        JSON.stringify(existing.tags ?? []) === JSON.stringify(item.tags ?? [])
      ) {
        return prev;
      }

      // Sustituimos si cambió o lo añadimos
      const filtered = prev.filter((i) => i.id !== item.id);
      return [...filtered, item];
    });
  };

  // 🔹 Eliminar un elemento del índice
  const unregisterItem = (id: string) => {
    setIndex((prev) => prev.filter((i) => i.id !== id));
  };

  // 🔹 Ejecutar búsqueda real (case insensitive)
const performSearch = (q?: string): SearchItem[] => {
  const queryStr = (q ?? query).trim().toLowerCase();
  if (!queryStr) return [];

  return index.filter((item) => {
    const tags = Array.isArray(item.tags)
      ? item.tags.map((t: string) => t.toLowerCase())
      : [];
    const haystack =
      `${item.title ?? ""} ${item.content ?? ""} ${tags.join(" ")}`;
    return haystack.toLowerCase().includes(queryStr);
  });
};


  // 🔹 Actualizar resultados automáticamente (con debounce)
  useEffect(() => {
    const id = setTimeout(() => setResults(performSearch()), 250);
    return () => clearTimeout(id);
  }, [query, index]);

  // 🔹 Memorizar valor del contexto para evitar renders innecesarios
  const value = useMemo(
    () => ({
      query,
      setQuery,
      results,
      registerItem,
      unregisterItem,
    }),
    [query, results, index]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};
